import { AppState, type AppStateData } from '../../core/AppState';
import { Toast } from '../Toast';

export class Sidebar {
  private container: HTMLElement;
  private state: AppState;

  constructor(container: HTMLElement, state: AppState) {
    this.container = container;
    this.state = state;

    // Layout is now handled by CSS (.sidebar)
    this.render(this.state.current);
    this.state.subscribe((data) => this.render(data));
  }

  private render(data: AppStateData) {
    this.container.innerHTML = '';


    // File Group
    const fileGroup = document.createElement('div');
    fileGroup.className = 'control-group';
    fileGroup.innerHTML = `
        <div class="control-label" style="font-size:0.75rem; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.8rem;">File</div>
        <div class="file-drop-area" id="dropArea" style="border:2px dashed var(--border); border-radius:6px; padding:1.5rem 1rem; text-align:center; cursor:pointer; position:relative; color:var(--text-muted);">
            <span id="fileName">${data.filename || 'Drop Image here'}</span>
            <input type="file" id="fileInput" title="" accept="image/jpeg,image/webp,image/png,image/gif" style="position:absolute; top:0; left:0; width:100%; height:100%; opacity:0; cursor:pointer;">
        </div>
        <button id="resetBtn" ${!data.buffer ? 'disabled' : ''} style="width:100%;">↺ Revert to Original</button>
    `;
    this.container.appendChild(fileGroup);

    // Convert Group (Phase 12)
    const convertGroup = document.createElement('div');
    convertGroup.className = 'control-group';
    convertGroup.innerHTML = `
        <div class="control-label" style="font-size:0.75rem; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.8rem;">Convert</div>
        <div style="display:flex; gap:0.5rem; margin-bottom:0.5rem;">
            <select id="convertFormat" style="flex:1; background:#2a2a2a; color:#eee; border:1px solid var(--border); padding:0.4rem; border-radius:4px;">
                <option value="image/jpeg">JPEG</option>
                <option value="image/png">PNG</option>
                <option value="image/webp">WebP</option>
            </select>
        </div>
        <button id="convertBtn" class="primary" ${!data.buffer ? 'disabled' : ''} style="width:100%;">Convert & Load</button>
    `;
    this.container.appendChild(convertGroup);

    // Event Listeners for File
    const dropArea = fileGroup.querySelector('#dropArea') as HTMLElement;
    const fileInput = fileGroup.querySelector('#fileInput') as HTMLInputElement;
    const resetBtn = fileGroup.querySelector('#resetBtn') as HTMLButtonElement;

    const convertBtn = convertGroup.querySelector('#convertBtn') as HTMLButtonElement;
    const convertFormat = convertGroup.querySelector('#convertFormat') as HTMLSelectElement;

    // ... (File listeners kept as is below, new convert listeners added)

    convertBtn.onclick = async () => {
      if (!this.state.current.buffer) return;

      const mime = convertFormat.value;
      const currentFormat = this.state.current.format; // JPEG, PNG, WEBP, GIF

      // Check for same format
      let targetFormatUpper = 'UNKNOWN';
      if (mime === 'image/jpeg') targetFormatUpper = 'JPEG';
      if (mime === 'image/png') targetFormatUpper = 'PNG';
      if (mime === 'image/webp') targetFormatUpper = 'WEBP';
      // Note: Parser returns 'WebP' usually, check case?
      // ParserRegistry uses class names or return values.
      // JpegParser -> 'JPEG', PngParser -> 'PNG', GifParser -> 'GIF', WebpParser -> 'WebP' ??
      // Let's normalize comparison.

      if (currentFormat && currentFormat.toUpperCase() === targetFormatUpper) {
        Toast.show(`Already in ${targetFormatUpper} format.`, 'info');
        return;
      }

      convertBtn.disabled = true;
      convertBtn.textContent = 'Converting...';

      try {
        let ext = 'jpg';
        if (mime === 'image/png') ext = 'png';
        if (mime === 'image/webp') ext = 'webp';

        const { ImageConverter } = await import('../../core/ImageConverter');
        const result = await ImageConverter.convert(this.state.current.buffer, { mime, ext });

        // Smart Filename Logic
        const oldName = this.state.current.filename || 'image';
        const isGenerated = this.state.current.isGenerated;

        // Strip extension
        let baseName = oldName.substring(0, oldName.lastIndexOf('.')) || oldName;

        // If generated, we assume the last "_converted" is ours, so remove it to avoid accumulation.
        // If NOT generated (uploaded), we strictly APPEND "_converted", even if it already has one.
        if (isGenerated) {
          if (baseName.endsWith('_converted')) {
            baseName = baseName.substring(0, baseName.length - 10);
          }
        }

        const newName = `${baseName}_converted.${ext}`;

        this.state.loadFile(result.buffer, newName, true);
        Toast.show('Conversion successful!', 'success');
      } catch (e) {
        Toast.show('Conversion failed: ' + e, 'error');
      } finally {
        convertBtn.disabled = false;
        convertBtn.textContent = 'Convert & Load';
      }
    };


    dropArea.ondragover = (e) => { e.preventDefault(); dropArea.style.borderColor = 'var(--accent)'; };
    dropArea.ondragleave = () => { dropArea.style.borderColor = 'var(--border)'; };
    dropArea.ondrop = async (e) => {
      e.preventDefault();
      dropArea.style.borderColor = 'var(--border)';
      if (e.dataTransfer && e.dataTransfer.files.length) {
        await this.handleFile(e.dataTransfer.files[0]);
      }
    };
    fileInput.onchange = async () => {
      if (fileInput.files && fileInput.files.length) {
        await this.handleFile(fileInput.files[0]);
      }
    };

    // Revert logic needs Original Buffer access.
    // Ideally AppState handles "Revert" by reloading original.
    // Wait, AppState only has 'buffer' in history.
    // If I want to revert, I need the original loaded buffer.
    // I should store original buffer in AppState or just reload?
    // Let's assume user re-drags for now OR we implement simple "Undo All" if history is deep.
    // Actually, PoC kept `originalBuffer`. My AppState only keeps History.
    // I should add `reloadOriginal` to AppState.
    // For now, let's just disabling it until implemented or use History Undo until index 0.

    resetBtn.onclick = () => {
      // Simple: Undo until start?
      // Better: AppState should store original.
      // Let's rely on Undo for this iteration or leave as placeholder.
      // Assuming "Revert" means "Reset History to state 0".
      while (this.state.canUndo()) {
        this.state.undo();
      }
    };

    // Status Group
    const statusGroup = document.createElement('div');
    statusGroup.className = 'control-group';
    statusGroup.innerHTML = `
        <div class="control-label" style="font-size:0.75rem; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.8rem;">Info</div>
        <div style="font-size:0.8rem; color:#888;">
            Format: <span style="color:#eee">${data.format || '-'}</span><br>
            Size: <span>${data.buffer ? data.buffer.length.toLocaleString() : '0'}</span> bytes<br>
            Markers: <span>${data.markers.length}</span>
        </div>
    `;
    this.container.appendChild(statusGroup);

    // Metadata Group
    if (data.metadata && Object.keys(data.metadata).length > 0) {
      const metaGroup = document.createElement('div');
      metaGroup.className = 'control-group';
      let metaHtml = '';
      for (const [key, val] of Object.entries(data.metadata)) {
        metaHtml += `<div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                <span style="color:#666;">${key}:</span>
                <span style="color:#aaa; text-align:right; max-width:150px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${val}">${val}</span>
            </div>`;
      }

      metaGroup.innerHTML = `
            <div class="control-label" style="font-size:0.75rem; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.8rem;">Metadata</div>
            <div style="font-size:0.75rem;">
                ${metaHtml}
            </div>
        `;
      this.container.appendChild(metaGroup);
    }
  }

  private async handleFile(file: File) {
    if (file.type !== 'image/jpeg' && file.type !== 'image/webp' && file.type !== 'image/png' && file.type !== 'image/gif') {
      Toast.show('Unsupported format.\nSupported format: JPEG, WebP, PNG, GIF', 'error');
      return;
    }
    const buf = await file.arrayBuffer();
    this.state.loadFile(new Uint8Array(buf), file.name);
  }
}
